import {
  Box, BoxProps, Button, Grid, useMediaQuery,
} from '@mui/material';
import React, {
  useCallback, useContext, useMemo, useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { changeRatePlan, setSelectedMeterID } from '../../reducers/user-input-reducer';
import { mapPlanToPalette } from '../../AppTheme';
import {
  IAppReduxState, IAccountReduxState, SwitchData,
} from '../../models';
import { CustomerService, SwitchService } from '../../services';
import { FeatureContext } from '../../FeatureContext';
import { updateCustomerDetail } from '../../reducers/account-reducer';

const ConfirmationActionsWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  padding: '1rem 16px 3.125rem 16px',
  [theme.breakpoints.up('sm')]: {
    padding: '1rem 32px 3.125rem 32px',
  },
}));

interface ConfirmationActionProps {
  selectedPlan: string;
  selectedMeterId: string;
}

export function ConfirmationActions({ selectedPlan, selectedMeterId }: ConfirmationActionProps) {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const palette = useMemo(() => mapPlanToPalette(selectedPlan), [selectedPlan]);
  const featureContext = useContext(FeatureContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const account = useSelector<IAppReduxState, IAccountReduxState>((state) => state.account);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const selectedServicePoint = useMemo(
    () => account?.servicePoints.find((sp) => sp.meterNumber === selectedMeterId),
    [account?.servicePoints, selectedMeterId],
  );

  const confirmHandler = useCallback(async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.preventDefault();
    if (!selectedServicePoint) return;

    try {
      setIsLoading(true);
      const status = await SwitchService.postSwitchData(
        {
          sp: selectedServicePoint?.servicePointId,
          newPlanCode: selectedPlan,
        } as SwitchData,
      );
      const response = await CustomerService.getCustomerDetail(featureContext.COMMERCIAL_RATES);
      if (response) {
        dispatch(updateCustomerDetail(response));
        dispatch(setSelectedMeterID(response.servicePoints[0].meterNumber));
      }
      if (status === 201) {
        dispatch(changeRatePlan({ selectedPlan: '' }));
        navigate('/congratulations');
      }
    } catch (err: any) {
      const errCode = err.response.data.message;
      window.dataLayer.push({
        event: 'Exception',
        error_text: errCode,
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, featureContext.COMMERCIAL_RATES, navigate, selectedPlan, selectedServicePoint]);

  const backHandler = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    dispatch(changeRatePlan({ selectedPlan: '' }));
    navigate('/plans');
  }, [dispatch, navigate]);

  return (
    <ConfirmationActionsWrapper>
      <Grid container direction={isSmallScreen ? 'column' : 'row'} alignItems="center" justifyContent="space-between" spacing={3}>
        <Grid item>
          <Button variant="outlined" color="primary" onClick={backHandler} disabled={isLoading}>
            <FormattedMessage id="confirm-plan-actions.goback" />
          </Button>
        </Grid>
        <Grid item>
          <LoadingButton
            variant="contained"
            color={palette}
            onClick={confirmHandler}
            loading={isLoading}
          >
            <FormattedMessage id="confirm-plan-actions.submit" />
          </LoadingButton>
        </Grid>
      </Grid>
    </ConfirmationActionsWrapper>
  );
}
